import { useMemo } from "react";
import MaterialTable from "material-table";
import type { Column } from "material-table";
import { Chip } from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import type {
  ReservationListItem,
  ReservationStatus,
} from "./types";
import styles from "./ReservationList.module.scss";

// Inline localization to avoid Jest module resolution issues with .js files
const materialTableES = {
  header: { actions: "" },
  body: {
    emptyDataSourceMessage: "",
    deleteTooltip: "Eliminar",
    editTooltip: "Editar",
    editRow: {
      deleteText: "¿Estas seguro que deseas eliminar este registro?",
      saveTooltip: "Guardar",
      cancelTooltip: "Cancelar",
    },
    filterRow: { filterTooltip: "Filtrar" },
  },
  pagination: {
    labelRowsSelect: "Filas",
    labelRowsPerPage: "Filas por página",
    firstAriaLabel: "Primera página",
    firstTooltip: "Primera página",
    previousAriaLabel: "Página anterior",
    previousTooltip: "Página anterior",
    nextAriaLabel: "Siguiente página",
    nextTooltip: "Siguiente página",
    lastAriaLabel: "Última página",
    lastTooltip: "Última página",
    labelDisplayedRows: "{from}-{to} de {count}",
  },
  toolbar: {
    searchTooltip: "Buscar",
    searchPlaceholder: "Buscar",
    nRowsSelected: "{0} productos seleccionados",
  },
};

interface ReservationListProps {
  data: ReservationListItem[];
  isLoading: boolean;
  /** Current user has reservations:approve permission */
  canApprove: boolean;
  /** Current user has reservations:edit_own permission */
  canEditOwn: boolean;
  onView: (reservation: ReservationListItem) => void;
  onEdit: (reservation: ReservationListItem) => void;
  onApprove: (reservation: ReservationListItem) => void;
  onReject: (reservation: ReservationListItem) => void;
}

const statusConfig: Record<
  ReservationStatus,
  { label: string; color: "default" | "primary" | "secondary" }
> = {
  pending: { label: "Pendiente", color: "default" },
  approved: { label: "Aprobada", color: "primary" },
  rejected: { label: "Rechazada", color: "secondary" },
};

export function getStatusChip(status: ReservationStatus) {
  const config = statusConfig[status] ?? { label: status ?? "Desconocido", color: "default" as const };
  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
}

export function ReservationList({
  data,
  isLoading,
  canApprove,
  canEditOwn,
  onView,
  onEdit,
  onApprove,
  onReject,
}: ReservationListProps) {
  const columns: Column<ReservationListItem>[] = useMemo(
    () => [
      {
        title: "Estado",
        field: "status",
        render: (rowData) => getStatusChip(rowData.status),
        cellStyle: { whiteSpace: "nowrap" },
      },
      { title: "Cotización", field: "quote_number" },
      {
        title: "Cliente",
        field: "client_name",
        render: (rowData) => rowData.client_name || "—",
      },
      { title: "Items", field: "items_count", type: "numeric" },
      { title: "Creado por", field: "created_by_name" },
      {
        title: "Fecha",
        field: "created_at",
        render: (rowData) =>
          new Date(rowData.created_at).toLocaleDateString("es-CO"),
      },
    ],
    []
  );

  return (
    <MaterialTable
      title=""
      columns={columns}
      data={data}
      isLoading={isLoading}
      actions={[
        {
          icon: () => <VisibilityIcon />,
          tooltip: "Ver detalle",
          onClick: (_event, rowData) => onView(rowData as ReservationListItem),
        },
        {
          icon: () => <EditIcon />,
          tooltip: "Editar",
          onClick: (_event: any, rowData: any) => onEdit(rowData as ReservationListItem),
          hidden: (rowData: ReservationListItem) =>
            !canEditOwn || rowData.status !== "pending",
        } as any,
        ...(canApprove
          ? [
              {
                icon: () => <CheckCircleIcon className={styles.approveIcon} />,
                tooltip: "Aprobar",
                onClick: (_event: any, rowData: any) =>
                  onApprove(rowData as ReservationListItem),
                hidden: (rowData: ReservationListItem) =>
                  rowData.status !== "pending",
              } as any,
              {
                icon: () => <CancelIcon className={styles.rejectIcon} />,
                tooltip: "Rechazar",
                onClick: (_event: any, rowData: any) =>
                  onReject(rowData as ReservationListItem),
                hidden: (rowData: ReservationListItem) =>
                  rowData.status !== "pending",
              } as any,
            ]
          : []),
      ]}
      options={{
        search: false,
        toolbar: false,
        paging: true,
        pageSize: 10,
        pageSizeOptions: [10, 20, 50],
        actionsColumnIndex: -1,
        padding: "dense",
        headerStyle: {
          backgroundColor: "#f4f6f8",
          color: "#637381",
          fontSize: "14px",
          fontWeight: 600,
          height: "3.5rem",
        },
        rowStyle: {
          fontSize: "14px",
          fontWeight: 400,
          color: "#1C252E",
        },
      }}
      localization={materialTableES}
    />
  );
}

export default ReservationList;
